import React, { useState, useRef } from 'react';
import {
    either,
    isEmpty,
    isNil,
    join,
    lensIndex,
    map,
    over,
    pick,
    pipe,
    prop,
    reject,
    splitAt
} from 'ramda';
import Form from 'js/components/common/Form';
import { useUserSelector } from 'js/store/hooks';
import LimitEdit from 'js/components/common/Fields/LimitEdit';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { money, ascending } from 'js/utils/Validation/validationRules';
import { usePortletConfig, usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';

import { setProductLimits } from './actions';
import { PRODUCT_ASCENDING_ERROR } from './constants';

import style from './index.scss';

export const prepareValidConfig = fields => fields.reduce((acc, field) => ({
    ...acc,
    [field]: [
        money(),
        ascending({ fields, error: PRODUCT_ASCENDING_ERROR })
    ]
}), {});

export const prepareTitle = pipe(
    splitAt(-1),
    over(lensIndex(0), join(', ')),
    reject(isEmpty),
    join(' & ')
);
export const useDefaultParams = () => ({ type: usePortletConfig().portletType });

export const Product = ({
    name,
    limit: productLimit,
    editMode,
    setEditMode,
    useParams = useDefaultParams
}) => {
    const translate = usePortletTranslations();
    const {
        config: {
            availablePeriods
        },
        fetchPortlet
    } = usePortletSelector(pick(['config', 'fetchPortlet']));
    const currency = useUserSelector(prop('currencyCode'));
    const { portletType: type } = usePortletConfig();
    const params = useParams();
    const limitsRef = useRef();

    const initialValues = {
        name,
        ...map(either(prop('newLimit'), prop('currentLimit')), productLimit)
    };

    const [validationConfig] = useState(prepareValidConfig(availablePeriods));

    const isInitialEdit = availablePeriods
        .map(period => initialValues[period])
        .every(value => isNil(value) || isEmpty(value));

    const onEditClick = reset => () => {
        reset();
        setEditMode(name);

        limitsRef.current?.focus();
    };

    const onCancelClick = reset => () => {
        if (editMode) {
            reset();
        }
        return setEditMode(null);
    };

    const submitHandler = async ({ name: products, ...periodValues }) => {
        const limits = pipe(
            map(period => ({
                products,
                timePeriod: period,
                amount: periodValues[period] || 0
            }))
        )(availablePeriods);

        try {
            await setProductLimits({ type, limits });

            setEditMode(null);
            fetchPortlet(params, { showLoader: false });
        } catch (error) {
            const { data = {} } = error?.response || error || {};
            const { errorCode, errorMessage, userErrors: { message } = {} } = data;
            return message || errorMessage || translate(`error.${errorCode}`);
        }
    };

    const classForEditMode = editMode ? style.editMode : '';

    return (
        <Form
            validateOnBlur={false}
            onSubmit={submitHandler}
            initialValues={initialValues}
            validationConfig={validationConfig}
            fieldsToReplaceComma={availablePeriods}
            render={({ handleSubmit, submitting, pristine, submitError, form: { reset } }) => (
                <form
                    onSubmit={event => event.preventDefault()}
                    className={`${style.limits} ${classForEditMode}`}
                >
                    <div tabIndex="-1" ref={limitsRef} className={style.limitsEdit}>
                        {availablePeriods.map(periodName => (
                            <LimitEdit
                                key={periodName}
                                currency={currency}
                                edit={editMode}
                                name={periodName}
                                {...productLimit[periodName]}
                            />
                        ))}
                    </div>
                    {submitError && <ErrorMessage error={submitError} animate={true} className={style.error} />}
                    <div className={style.titleWrapper}>
                        <span className={style.title}>{prepareTitle(name)}</span>
                        {editMode
                            ? (
                                <div className={style.btnsHolder}>
                                    <button
                                        type="button"
                                        className={style.cancelBtn}
                                        onClick={onCancelClick(reset)}
                                    >
                                        {translate('Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className={style.saveBtn}
                                        disabled={submitting || pristine}
                                    >
                                        {translate('Save')}
                                    </button>
                                </div>
                            )
                            : (
                                <button
                                    type="button"
                                    className={style.editBtn}
                                    onClick={onEditClick(reset)}
                                >
                                    {isInitialEdit ? translate('Setup') : translate('Edit')}
                                </button>
                            )
                        }
                    </div>
                </form>
            )}
        />
    );
};

export default Product;
